import type {PageObject} from '@PosterWhiteboard/page/page.types';
import type {UserObject} from '@PosterWhiteboard/user/user.types';
import type {PosterTypeObject} from '@PosterWhiteboard/poster-type/poster-type.types';
import type {GridObject} from '@PosterWhiteboard/poster/grid.class';
import type {BleedObject} from '@PosterWhiteboard/poster/bleed.class';
import type {FoldObject} from '@PosterWhiteboard/poster/fold.class';
import type {FolderObject} from '@PosterWhiteboard/classes/folder.class';
import type {PosterScalingObject} from '@PosterWhiteboard/poster/poster-scaling.class';
import type {DrawingObject} from '@PosterWhiteboard/poster/poster-drawing/poster-drawing';
import type {PosterModeObject} from '@PosterWhiteboard/poster/poster-mode.class';
import type {ItemsMultiSelectObject} from '@PosterWhiteboard/poster/items-multi-select.class';
import type {TextItemObject} from '@PosterWhiteboard/items/text-item/text-item.types';

export const MOBILE_WHITEBOARD_PADDING = 20;

export const EDITOR_SMALL_SCREEN_WIDTH_THRESHOLD = 780;

export enum MobilePageSelectionModeState {
  INACTIVE = 'inactive',
  TRANSITIONING = 'transitioning',
  ACTIVE = 'active',
}

export const POSTER_VERSION = {
  CURRENT: 61,
  SHADOW_IMPROVEMENT: 15,
  FABRIC_3_UPDATE: 12,
  FABRIC_2_UPDATE: 11,
  HTML5: 10,
  LEGACY: 3,
};

export const PAGE_END_TIME_TOLERANCE_SECONDS = 0.05;

/**
 * Items use exclusive `onPageEndTime` (visible while currentTime < onPageEndTime), so reporting
 * a clock time exactly at the page duration hides items that end at the page boundary. Cap the
 * clock and any seek that targets the page end this much short of the duration so those items
 * stay rendered. The size is well below human-perceptible time; it just absorbs float rounding.
 */
export const PAGE_END_SEEK_EPSILON = 0.0001;

export interface PosterEvents {
  seeked: never;
  'textSlide:added': string;
  'fabricCanvas:touch:start': never;
  modified: never;
  'colors:extracted': never;
}

export interface PosterPageObject {
  pagesHashMap: Record<string, PageObject>;
  pageOrder: string[];
}

export interface PosterLoadObject {
  hashedID: string;
  type: PosterTypeObject;
  userWidth: number;
  userHeight: number;
  units: string;
  version: number;
  name: string;
  width: number;
  height: number;
  description: string;
  isTemplate: boolean;
  isPublic: boolean;
  isCopyable: boolean;
  isPurchaseable: boolean;
  createdOn: number;
  lastModified: number;
  idLastModifier: string;
  isInternal: boolean;
  seoName: string;
  id: string;
  creator?: UserObject;
  owner?: UserObject;
  pages: PosterPageObject;
}

export interface ResizeDimensions {
  left: number;
  top: number;
  scaleFactor: number;
}

export interface PosterObject extends PosterLoadObject {
  scaling: PosterScalingObject;
  duration: number;
  bleed: BleedObject;
  folder: FolderObject;
  grid: GridObject;
  mode: PosterModeObject;
  fold: FoldObject;
  drawing: DrawingObject;
  itemsMultiSelect: ItemsMultiSelectObject;
  activePageId: string;
  copyKey: string;
  isInitialized: boolean;
  uploadingImages: string[];
}

export enum GettyAssetType {
  IMAGE = 'image',
  VIDEO = 'video',
  EXTRACTED_STICKER = 'extractedSticker',
}

export const POSTER_GETTY_LIMIT: Record<GettyAssetType, number> = {
  [GettyAssetType.IMAGE]: 12,
  [GettyAssetType.VIDEO]: 3,
  [GettyAssetType.EXTRACTED_STICKER]: 20,
};

export interface GettyAssetClassification {
  gettyType: GettyAssetType;
  identityKey: string;
}

export interface GettyLimitFilterResult<T> {
  allowed: T[];
  droppedTypes: Set<GettyAssetType>;
}

export const MAX_POSTER_NAME_LENGTH = 100;

export type AiTextMorphChanges = Partial<TextItemObject>;

export interface AiTextMorphResponse {
  textContentChanges: Record<string, AiTextMorphChanges>;
}
